import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Play, Pause, Square, Download, Trash2, ArrowUpDown, FileAudio, Lock, FileText } from "lucide-react";
import { TranscriptionDialog } from "@/components/subtitles";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ToastAction } from "@/components/ui/toast";
import { useRecordings, Recording } from "@/hooks/useRecordings";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import { useSubscription } from "@/hooks/useSubscription";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow, Locale } from "date-fns";
import { zhTW, zhCN, enUS, ja, ko } from "date-fns/locale";

type SortKey = "filename" | "duration" | "file_size" | "created_at";
type SortOrder = "asc" | "desc";

export function RecordingsTable() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const locales: Record<string, Locale> = {
    "zh-TW": zhTW,
    "zh-CN": zhCN,
    "en": enUS,
    "ja": ja,
    "ko": ko,
  };

  const { recordings, loading, fetchRecordings, deleteRecording, getRecordingUrl } = useRecordings();
  const { isTrialExpired, isGracePeriodExpired, isInGracePeriod, gracePeriodDaysRemaining } = useTrialStatus();
  const { currentPlan } = useSubscription();
  
  // 免費用戶：寬限期過後才禁止下載（試用過期後仍有 3 天可以下載）
  const isDownloadDisabled = currentPlan === 'free' && isGracePeriodExpired;
  
  const [sortKey, setSortKey] = useState<SortKey>("created_at");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [transcriptionRecording, setTranscriptionRecording] = useState<Recording | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlCache = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    fetchRecordings();
  }, [fetchRecordings]);

  // Play audio when URL is set (autoPlay may be blocked by browser)
  useEffect(() => {
    if (audioUrl && audioRef.current) {
      audioRef.current.play().catch(err => {
        console.error('Audio playback failed:', err);
      });
    }
  }, [audioUrl]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  const sortedRecordings = [...recordings].sort((a, b) => {
    let comparison = 0;
    switch (sortKey) {
      case "filename":
        comparison = a.filename.localeCompare(b.filename);
        break;
      case "duration":
        comparison = a.duration - b.duration;
        break;
      case "file_size":
        comparison = a.file_size - b.file_size;
        break;
      case "created_at":
        comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        break;
    }
    return sortOrder === "asc" ? comparison : -comparison;
  });

  const totalPages = Math.ceil(sortedRecordings.length / itemsPerPage);
  const paginatedRecordings = sortedRecordings.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const SortableHeader = ({ label, sortKeyName }: { label: string; sortKeyName: SortKey }) => (
    <Button
      variant="ghost"
      className="h-auto p-0 hover:bg-transparent"
      onClick={() => handleSort(sortKeyName)}
    >
      <span className="flex items-center gap-1">
        {label}
        <ArrowUpDown className="h-4 w-4" />
      </span>
    </Button>
  );

  const formatDuration = (seconds: number) => {
    const min = Math.floor(seconds / 60);
    const sec = Math.floor(seconds % 60);
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handlePlay = async (recording: Recording) => {
    // If same recording, toggle pause/play
    if (playingId === recording.id) {
      if (audioRef.current) {
        if (audioRef.current.paused) {
          audioRef.current.play();
          setIsPaused(false);
        } else {
          audioRef.current.pause();
          setIsPaused(true);
        }
      }
      return;
    }
    
    // Stop current audio if any
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    
    // Check cache first for faster playback
    const cachedUrl = urlCache.current.get(recording.storage_path);
    if (cachedUrl) {
      setAudioUrl(cachedUrl);
      setPlayingId(recording.id);
      setProgress(0);
      setCurrentTime(0);
      return;
    }
    
    // Fetch URL with loading state
    setLoadingId(recording.id);
    const url = await getRecordingUrl(recording.storage_path);
    setLoadingId(null);
    
    if (url) {
      urlCache.current.set(recording.storage_path, url);
      setAudioUrl(url);
      setPlayingId(recording.id);
      setProgress(0);
      setCurrentTime(0);
    }
  };

  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlayingId(null);
    setAudioUrl(null);
    setProgress(0);
    setCurrentTime(0);
    setIsPaused(false);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const current = audioRef.current.currentTime;
      const duration = audioRef.current.duration;
      setCurrentTime(current);
      setProgress(duration > 0 ? (current / duration) * 100 : 0);
    }
  };

  const handleAudioEnded = () => {
    setPlayingId(null);
    setProgress(0);
    setCurrentTime(0);
    setIsPaused(false);
  };

  const handleDownload = async (recording: Recording) => {
    // 寬限期過後禁止下載
    if (isDownloadDisabled) {
      toast({
        title: t("recordings.trialExpired.title") || "試用已過期",
        description: t("recordings.trialExpired.downloadDescription") || "您的免費試用已結束，請升級至付費方案以下載錄音",
        variant: "destructive",
        action: (
          <ToastAction 
            altText={t("recordings.trialExpired.upgrade") || "升級"}
            onClick={() => navigate("/#pricing")}
          >
            {t("recordings.trialExpired.upgrade") || "升級"}
          </ToastAction>
        ),
      });
      return;
    }
    
    // 寬限期內提示用戶盡快下載
    if (currentPlan === 'free' && isInGracePeriod) {
      toast({
        title: t("recordings.gracePeriod.title") || "寬限期提醒",
        description: t("recordings.gracePeriod.description", { days: gracePeriodDaysRemaining }) || 
          `您的試用已過期，還有 ${gracePeriodDaysRemaining} 天可下載錄音`,
        variant: "default",
        action: (
          <ToastAction 
            altText={t("recordings.trialExpired.upgrade") || "升級"}
            onClick={() => navigate("/#pricing")}
          >
            {t("recordings.trialExpired.upgrade") || "升級"}
          </ToastAction>
        ),
      });
      // 繼續下載，不 return
    }
    
    setDownloadingId(recording.id);
    const url = await getRecordingUrl(recording.storage_path);
    if (url) {
      try {
        // Fetch the file as blob to enable proper download (cross-origin URLs can't use download attribute directly)
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = recording.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        
        // Clean up blob URL after download
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      } catch (err) {
        console.error('Download failed:', err);
      }
    }
    setDownloadingId(null);
  };

  if (loading && recordings.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
        </CardContent>
      </Card>
    );
  }

  return (<>
    <Card className="border-2 border-black shadow-[4px_4px_0_#1A1A1A]">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle>{t("recordings.title")}</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {recordings.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {t("recordings.empty")}
          </div>
        ) : (
          <>
            {/* Single global audio element - prevents duplicate audio on mobile/desktop */}
            {playingId && audioUrl && (
              <audio 
                ref={audioRef}
                src={audioUrl} 
                autoPlay 
                className="hidden" 
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleAudioEnded}
              />
            )}
            
            {/* Mobile Card Layout */}
            <div className="md:hidden space-y-3">
              {paginatedRecordings.map((recording) => (
                <div 
                  key={recording.id} 
                  className="border-2 border-black rounded-xl bg-white p-4 space-y-3"
                >
                  {/* Filename Row with waveform */}
                  <div className="flex items-center gap-2">
                    {playingId === recording.id ? (
                      <div className="flex items-end gap-0.5 h-4 w-5">
                        <div className="w-1 bg-[#FF6B9D] rounded-sm" style={{ height: '40%', animation: 'waveform 1.2s ease-in-out infinite', animationDelay: '0ms', animationPlayState: isPaused ? 'paused' : 'running' }} />
                        <div className="w-1 bg-[#FF6B9D] rounded-sm" style={{ height: '70%', animation: 'waveform 1.2s ease-in-out infinite', animationDelay: '200ms', animationPlayState: isPaused ? 'paused' : 'running' }} />
                        <div className="w-1 bg-[#FF6B9D] rounded-sm" style={{ height: '50%', animation: 'waveform 1.2s ease-in-out infinite', animationDelay: '400ms', animationPlayState: isPaused ? 'paused' : 'running' }} />
                        <div className="w-1 bg-[#FF6B9D] rounded-sm" style={{ height: '90%', animation: 'waveform 1.2s ease-in-out infinite', animationDelay: '600ms', animationPlayState: isPaused ? 'paused' : 'running' }} />
                      </div>
                    ) : (
                      <FileAudio className="w-4 h-4 text-primary flex-shrink-0" />
                    )}
                    <h3 className="font-bold text-base truncate flex-1">{recording.filename}</h3>
                  </div>
                  
                  {/* Progress bar when playing */}
                  {playingId === recording.id && (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-gray-200 border border-black rounded-full overflow-hidden">
                        <div className="h-full bg-[#FF6B9D] transition-all duration-200" style={{ width: `${progress}%` }} />
                      </div>
                      <span className="text-xs tabular-nums text-muted-foreground min-w-[36px]">
                        {formatDuration(currentTime)}
                      </span>
                    </div>
                  )}
                  
                  {/* Info Row */}
                  <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                    <span>{formatDuration(recording.duration)}</span>
                    <span>{formatFileSize(recording.file_size)}</span>
                    <span>{formatDistanceToNow(new Date(recording.created_at), { addSuffix: true, locale: locales[i18n.language] || enUS })}</span>
                  </div>
                  
                  {/* Actions Row */}
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                    {/* Play/Pause Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={loadingId === recording.id}
                      className={`h-9 w-9 p-0 border-2 border-black rounded-lg transition-all
                        ${playingId === recording.id 
                          ? 'bg-[#FF6B9D] text-black' 
                          : loadingId === recording.id
                            ? 'bg-[#4DA6FF]'
                            : 'bg-white hover:bg-[#FFD93D] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A]'
                        }`}
                      onClick={() => handlePlay(recording)}
                    >
                      {loadingId === recording.id ? (
                        <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      ) : playingId === recording.id && !isPaused ? (
                        <Pause className="h-4 w-4 fill-current" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </Button>
                    
                    {/* Stop Button */}
                    {playingId === recording.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-9 w-9 p-0 border-2 border-black rounded-lg bg-white hover:bg-[#FF6B9D] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A] transition-all"
                        onClick={handleStop}
                      >
                        <Square className="h-4 w-4 fill-current" />
                      </Button>
                    )}
                    
                    {/* Download Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={downloadingId === recording.id || isDownloadDisabled}
                      className={`h-9 w-9 p-0 border-2 border-black rounded-lg transition-all
                        ${isDownloadDisabled
                          ? 'bg-gray-200 cursor-not-allowed opacity-50'
                          : downloadingId === recording.id
                            ? 'bg-[#4DA6FF]'
                            : 'bg-white hover:bg-[#4DA6FF] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A]'
                        }`}
                      onClick={() => handleDownload(recording)}
                      title={isDownloadDisabled ? t("recordings.trialExpired.title") || "試用已過期" : ""}
                    >
                      {downloadingId === recording.id ? (
                        <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      ) : isDownloadDisabled ? (
                        <Lock className="h-4 w-4 text-gray-500" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                    </Button>
                    
                    {/* Subtitle Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-9 w-9 p-0 border-2 border-black rounded-lg bg-white hover:bg-[#6FEDD6] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A] transition-all"
                      onClick={() => setTranscriptionRecording(recording)}
                      title={t("recordings.actions.subtitle") || "字幕"}
                    >
                      <FileText className="h-4 w-4" />
                    </Button>
                    
                    {/* Delete Button */}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-9 w-9 p-0 border-2 border-black rounded-lg bg-white hover:bg-[#FF6B9D] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A] transition-all"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t("recordings.deleteConfirm.title")}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {t("recordings.deleteConfirm.description")}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t("recordings.deleteConfirm.cancel")}</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteRecording(recording)}>
                            {t("recordings.deleteConfirm.confirm")}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table Layout */}
            <div className="hidden md:block border-2 border-black rounded-xl overflow-hidden">
              <Table>
                <TableHeader className="bg-gray-100 border-b-2 border-black">
                  <TableRow>
                    <TableHead className="font-bold text-black">
                      <SortableHeader label={t("recordings.columns.filename")} sortKeyName="filename" />
                    </TableHead>
                    <TableHead className="font-bold text-black">
                      <SortableHeader label={t("recordings.columns.duration")} sortKeyName="duration" />
                    </TableHead>
                     <TableHead className="font-bold text-black">
                      <SortableHeader label={t("recordings.columns.size")} sortKeyName="file_size" />
                    </TableHead>
                    <TableHead className="font-bold text-black">
                      <SortableHeader label={t("recordings.columns.createdAt")} sortKeyName="created_at" />
                    </TableHead>
                    <TableHead className="text-right font-bold text-black">{t("recordings.columns.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="divide-y-2 divide-black bg-white">
                {paginatedRecordings.map((recording) => (
                  <TableRow key={recording.id} className="group hover:bg-muted/50 transition-colors">
                    <TableCell className="font-medium min-w-[200px]">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          {playingId === recording.id ? (
                            <div className="flex items-end gap-0.5 h-4 w-5">
                              <div className="w-1 bg-[#FF6B9D] rounded-sm" style={{ height: '40%', animation: 'waveform 1.2s ease-in-out infinite', animationDelay: '0ms', animationPlayState: isPaused ? 'paused' : 'running' }} />
                              <div className="w-1 bg-[#FF6B9D] rounded-sm" style={{ height: '70%', animation: 'waveform 1.2s ease-in-out infinite', animationDelay: '200ms', animationPlayState: isPaused ? 'paused' : 'running' }} />
                              <div className="w-1 bg-[#FF6B9D] rounded-sm" style={{ height: '50%', animation: 'waveform 1.2s ease-in-out infinite', animationDelay: '400ms', animationPlayState: isPaused ? 'paused' : 'running' }} />
                              <div className="w-1 bg-[#FF6B9D] rounded-sm" style={{ height: '90%', animation: 'waveform 1.2s ease-in-out infinite', animationDelay: '600ms', animationPlayState: isPaused ? 'paused' : 'running' }} />
                            </div>
                          ) : (
                            <FileAudio className="w-4 h-4 text-primary" />
                          )}
                          <span className="truncate max-w-[300px]">{recording.filename}</span>
                        </div>
                        {playingId === recording.id && (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-gray-200 border border-black rounded-full overflow-hidden">
                              <div className="h-full bg-[#FF6B9D] transition-all duration-200" style={{ width: `${progress}%` }} />
                            </div>
                            <span className="text-xs tabular-nums text-muted-foreground min-w-[36px]">
                              {formatDuration(currentTime)}
                            </span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="tabular-nums text-muted-foreground whitespace-nowrap">
                       {formatDuration(recording.duration)}
                    </TableCell>
                    <TableCell className="tabular-nums text-muted-foreground whitespace-nowrap">
                       {formatFileSize(recording.file_size)}
                    </TableCell>
                    <TableCell className="text-muted-foreground whitespace-nowrap text-sm">
                      {new Date(recording.created_at).toLocaleDateString(i18n.language)}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <div className="flex justify-end gap-3">
                         
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={loadingId === recording.id}
                          className={`h-8 w-8 p-0 border-2 border-black rounded-lg transition-all
                            ${playingId === recording.id 
                              ? 'bg-[#FF6B9D] text-black' 
                              : loadingId === recording.id
                                ? 'bg-[#4DA6FF]'
                                : 'bg-white hover:bg-[#FFD93D] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A]'
                            }`}
                          onClick={() => handlePlay(recording)}
                        >
                          {loadingId === recording.id ? (
                            <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                          ) : playingId === recording.id && !isPaused ? (
                            <Pause className="h-4 w-4 fill-current" />
                          ) : (
                            <Play className="h-4 w-4" />
                          )}
                        </Button>
                        
                        {playingId === recording.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 border-2 border-black rounded-lg bg-white hover:bg-[#FF6B9D] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A] transition-all"
                            onClick={handleStop}
                          >
                            <Square className="h-4 w-4 fill-current" />
                          </Button>
                        )}
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={downloadingId === recording.id || isDownloadDisabled}
                          className={`h-8 w-8 p-0 border-2 border-black rounded-lg transition-all
                            ${isDownloadDisabled
                              ? 'bg-gray-200 cursor-not-allowed opacity-50'
                              : downloadingId === recording.id
                                ? 'bg-[#4DA6FF]'
                                : 'bg-white hover:bg-[#4DA6FF] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A]'
                            }`}
                          onClick={() => handleDownload(recording)}
                          title={isDownloadDisabled ? t("recordings.trialExpired.title") || "試用已過期" : ""}
                        >
                          {downloadingId === recording.id ? (
                            <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                          ) : isDownloadDisabled ? (
                            <Lock className="h-4 w-4 text-gray-500" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </Button>
                        
                        {/* Subtitle Button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 border-2 border-black rounded-lg bg-white hover:bg-[#6FEDD6] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A] transition-all"
                          onClick={() => setTranscriptionRecording(recording)}
                          title={t("recordings.actions.subtitle") || "字幕"}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-8 w-8 p-0 border-2 border-black rounded-lg bg-white hover:bg-[#FF6B9D] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A] transition-all"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                            <AlertDialogTitle>{t("recordings.deleteConfirm.title")}</AlertDialogTitle>
                            <AlertDialogDescription>
                              {t("recordings.deleteConfirm.description")}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t("recordings.deleteConfirm.cancel")}</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteRecording(recording)}>
                              {t("recordings.deleteConfirm.confirm")}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                </TableBody>
              </Table>
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="h-8 border-2 border-black rounded-lg bg-white hover:bg-[#FFD93D] whitespace-nowrap"
                >
                  {t("recordings.pagination.previous")}
                </Button>
                <span className="flex items-center px-4 text-sm">
                  {currentPage} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="h-8 border-2 border-black rounded-lg bg-white hover:bg-[#FFD93D] whitespace-nowrap"
                >
                  {t("recordings.pagination.next")}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>

      <TranscriptionDialog
        isOpen={!!transcriptionRecording}
        onClose={() => setTranscriptionRecording(null)}
        recording={transcriptionRecording}
      />
    </>);
}
